// src/components/LoginForm.js
import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebaseConfig';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useOrganization } from '../contexts/OrganizationContext';
import NotificationService from '../services/NotificationService.tsx';
import { validateEmail } from '../utils/validationUtils';
import PlatformLayout from './PlatformLayout';
import PlatformInput from './PlatformInput';
import PlatformButton from './PlatformButton';
import PlatformText from './PlatformText';
import ErrorBoundary from './ErrorBoundary';
import { Capacitor } from '@capacitor/core';
import { isIOS, isAndroid } from '../utils/platformUtils';
import { useTranslation } from '../hooks/useTranslation';
import styles from './LoginForm.module.css';
import forgotPasswordStyles from './ForgotPasswordForm.module.css';
import LanguageSwitcher from './LanguageSwitcher';

function LoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const isNative = Capacitor.isNativePlatform();
  const isNativeIOS = isNative && isIOS();
  const { loading: orgLoading, error: orgError } = useOrganization();
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const [credentials, setCredentials] = useState({ 
    email: '', 
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [orgCodeMessage, setOrgCodeMessage] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  // Set initial messages from navigation state
  useEffect(() => {
    if (location.state?.message) {
      if (location.state.isOrgCode) {
        setOrgCodeMessage(location.state.message);
      } else {
        setSuccessMessage(location.state.message);
      }
    }
  }, [location.state]);

  // Clear regular success message after 5 seconds, but keep org code message
  useEffect(() => {
    if (successMessage && !location.state?.isOrgCode) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials({...credentials, [name]: value});
    setError(''); // Clear error when user starts typing
    setFieldErrors(prev => ({ ...prev, [name]: '' })); // Clear field error
    
    // Real-time validation for email
    if (name === 'email' && value) {
      const emailValidation = validateEmail(value);
      if (!emailValidation.isValid) {
        setFieldErrors(prev => ({ ...prev, email: emailValidation.error }));
      }
    }
  };

  useEffect(() => {
    if (orgError) {
      setError(orgError);
    }
  }, [orgError]);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      setError(t('passwordReset.error.emailRequired'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setSuccessMessage(t('passwordReset.success.resetEmailSent'));
      setShowResetForm(false);
      setResetEmail('');
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        setError(t('passwordReset.error.emailNotFound'));
      } else {
        setError(t('passwordReset.error.generic'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setFieldErrors({});
    
    // Basic email validation
    const emailValidation = validateEmail(credentials.email);
    if (!emailValidation.isValid) {
      setFieldErrors({ email: emailValidation.error });
      setIsLoading(false);
      return;
    }
    
    try {
      // First sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
      const user = userCredential.user;

      // Get user document
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();

      if (!userData?.organizationId) {
        await auth.signOut();
        setError(t('login.error.noOrganization'));
        setIsLoading(false);
        return;
      }

      // Get organization document
      const orgDoc = await getDoc(doc(db, 'organizations', userData.organizationId));
      
      if (!orgDoc.exists()) {
        await auth.signOut();
        setError(t('login.error.organizationNotFound'));
        setIsLoading(false);
        return;
      }


      
      // Get user's role from organization
      const orgUserDoc = await getDoc(doc(db, `organizations/${userData.organizationId}/users/${user.uid}`));
      
      if (!orgUserDoc.exists()) {
        await auth.signOut();
        setError(t('login.error.userNotFoundInOrg'));
        setIsLoading(false);
        return;
      }

      // ** Add Notification Logic Here **
      const notificationService = NotificationService.getInstance();
      notificationService.setCurrentUser(user.uid);
      notificationService.setCurrentOrganization(userData.organizationId);
      notificationService.initialize();
      // ** End Notification Logic **

      // If everything is valid, let the user in
      // The OrganizationContext will handle the rest
      navigate('/orders');
    } catch (error) {
      // Handle specific Firebase Auth error codes
      switch (error.code) {
        case 'auth/invalid-email':
          setError(t('login.error.invalidEmail'));
          break;
        case 'auth/user-disabled':
          setError(t('login.error.userDisabled'));
          break;
        case 'auth/user-not-found':
          setError(t('login.error.userNotFound'));
          break;
        case 'auth/wrong-password':
          setError(t('login.error.wrongPassword'));
          break;
        case 'auth/too-many-requests':
          setError(t('login.error.tooManyRequests'));
          break;
        case 'auth/invalid-credential':
          setError(t('login.error.invalidCredentials'));
          break;
        case 'auth/network-request-failed':
          setError(t('login.error.networkError'));
          break;
        default:
          setError(t('login.error.generic'));
      }
      setIsLoading(false);
    }
  };

  // Render different layouts based on platform
  if (isNative) {
    return (
      <PlatformLayout>
        <div className={styles.formContainer}>
          <div className={`${styles.formCard} fade-in`}>
            {/* Language Switcher - Subtle placement */}
            <LanguageSwitcher 
              variant="toggle" 
              size="small" 
              position="top-right"
            />
            
            {!showResetForm && (
              <div style={{ marginBottom: '32px' }}>
                <h1 className={styles.formTitle}>{t('login.title')}</h1>
                <p className={styles.formSubtitle}>{t('login.subtitle')}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ width: '100%' }} className={`${styles.formWrapper} form-simple`}>
              {error && (
                <div style={{
                  backgroundColor: isIOS() ? '#FFE5E5' : isAndroid() ? '#FFEBEE' : '#FFEBEE',
                  color: isIOS() ? '#FF3B30' : isAndroid() ? '#B00020' : '#D32F2F',
                  padding: '12px 16px',
                  borderRadius: isIOS() ? '10px' : isAndroid() ? '4px' : '8px',
                  marginBottom: '16px',
                  fontSize: '14px',
                  border: `1px solid ${isIOS() ? '#FF3B30' : isAndroid() ? '#B00020' : '#D32F2F'}`
                }}>
                  {error}
                </div>
              )}
              
              {orgCodeMessage && (
                <div style={{
                  backgroundColor: isIOS() ? '#E8F5E9' : isAndroid() ? '#E8F5E9' : '#E8F5E9',
                  color: isIOS() ? '#2E7D32' : isAndroid() ? '#2E7D32' : '#2E7D32',
                  padding: '12px 16px',
                  borderRadius: isIOS() ? '10px' : isAndroid() ? '4px' : '8px',
                  marginBottom: '16px',
                  fontSize: '14px',
                  border: `1px solid ${isIOS() ? '#4CAF50' : isAndroid() ? '#4CAF50' : '#4CAF50'}`
                }}>
                  {orgCodeMessage}
                </div>
              )}
              
              {successMessage && !orgCodeMessage && (
                <div style={{
                  backgroundColor: isIOS() ? '#E8F5E9' : isAndroid() ? '#E8F5E9' : '#E8F5E9',
                  color: isIOS() ? '#2E7D32' : isAndroid() ? '#2E7D32' : '#2E7D32',
                  padding: '12px 16px',
                  borderRadius: isIOS() ? '10px' : isAndroid() ? '4px' : '8px',
                  marginBottom: '16px',
                  fontSize: '14px',
                  border: `1px solid ${isIOS() ? '#4CAF50' : isAndroid() ? '#4CAF50' : '#4CAF50'}`
                }}>
                  {successMessage}
                </div>
              )}

              {!showResetForm ? (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <PlatformInput
                      type="email"
                      name="email"
                      placeholder={t('login.email')}
                      value={credentials.email}
                      onChange={handleChange}
                      required
                    />
                    {fieldErrors.email && (
                      <div style={{
                        color: isIOS() ? '#FF3B30' : isAndroid() ? '#B00020' : '#D32F2F',
                        fontSize: '12px',
                        marginTop: '4px',
                        marginLeft: '4px'
                      }}>
                        {fieldErrors.email}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ marginBottom: '3px' }}>
                    <PlatformInput
                      type="password"
                      name="password"
                      placeholder={t('login.password')}
                      value={credentials.password}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div style={{ marginBottom: '13px', width: '100%', textAlign: 'left' }}>
                    <span
                      role="link"
                      tabIndex={0}
                      style={{
                        color: isIOS() ? '#007AFF' : isAndroid() ? '#007AFF' : '#1976D2',
                        fontSize: '14px',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontWeight: 600
                      }}
                      onClick={e => {
                        e.preventDefault();
                        setShowResetForm(true);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setShowResetForm(true);
                        }
                      }}
                    >
                      {t('login.forgotPassword')}
                    </span>
                  </div>

                  <div style={{ marginBottom: '2px' }}>
                    <PlatformButton
                      type="submit"
                      disabled={isLoading}
                      style={{ width: '100%' }}
                    >
                      {isLoading ? t('login.signingIn') : t('login.signIn')}
                    </PlatformButton>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginTop: '4px', width: '100%' }}>
                    <div style={{ textAlign: 'center' }}>
                      <span style={{ color: isIOS() ? '#8E8E93' : isAndroid() ? '#757575' : '#666666' }}>
                        {t('login.noAccount')}{' '}
                      </span>
                      <Link
                        to="/register"
                        style={{
                          color: isIOS() ? '#007AFF' : isAndroid() ? '#007AFF' : '#1976D2',
                          textDecoration: 'none',
                          fontWeight: '600'
                        }}
                      >
                        {t('login.signUp')}
                      </Link>
                    </div>
                    {!isNativeIOS && (
                      <div style={{ textAlign: 'center' }}>
                        <span style={{ color: isIOS() ? '#8E8E93' : isAndroid() ? '#757575' : '#666666' }}>
                          {t('login.createOrganization')}{' '}
                        </span>
                        <Link
                          to="/register-organization"
                          style={{
                            color: isIOS() ? '#007AFF' : isAndroid() ? '#007AFF' : '#1976D2',
                            textDecoration: 'none',
                            fontWeight: '600'
                          }}
                        >
                          {t('login.registerOrganization')}
                        </Link>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className={forgotPasswordStyles.formWrapper}>
                    <h1 className={forgotPasswordStyles.formTitle}>{t('passwordReset.title')}</h1>
                    <p className={forgotPasswordStyles.formSubtitle}>{t('passwordReset.subtitle')}</p>
                    
                    <div className={forgotPasswordStyles.field}>
                      <label htmlFor="resetEmail">{t('passwordReset.email')}</label>
                      <PlatformInput
                        type="email"
                        id="resetEmail"
                        placeholder={t('passwordReset.email')}
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                      />
                    </div>

                    <div className={forgotPasswordStyles.actions}>
                      <PlatformButton
                        type="submit"
                        className={forgotPasswordStyles.submit}
                        onClick={handlePasswordReset}
                        disabled={isLoading}
                      >
                        {isLoading ? t('passwordReset.sendingReset') : t('passwordReset.sendReset')}
                      </PlatformButton>
                      <PlatformButton
                        type="button"
                        className={forgotPasswordStyles.cancel}
                        onClick={() => setShowResetForm(false)}
                        disabled={isLoading}
                      >
                        {tCommon('cancel')}
                      </PlatformButton>
                    </div>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </PlatformLayout>
    );
  }

  // Web version (existing code)
  return (
    <ErrorBoundary componentName="LoginForm">
      <PlatformLayout>
      <div className="container">
      <div className={!isNative ? "auth-container" : ""}>
        {/* Language Switcher - Subtle placement for web */}
        <LanguageSwitcher 
          variant="toggle" 
          size="small" 
          position="top-right"
        />
        
        {!showResetForm && (
          <div className="auth-header">
              <PlatformText variant="h1" translationKey="login.title" namespace="auth">Welcome Back</PlatformText>
              <PlatformText variant="body" className="auth-subtitle" translationKey="login.subtitle" namespace="auth">Please log in to your account</PlatformText>
          </div>
        )}

        <form onSubmit={handleSubmit} className={`${styles.formWrapper} auth-form form-simple`}>
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}
          {orgCodeMessage && (
            <div className="org-code-message" style={{ 
              backgroundColor: '#e8f5e9',
              color: '#2e7d32',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              whiteSpace: 'pre-line',
              border: '1px solid #81c784'
            }}>
              {orgCodeMessage}
            </div>
          )}
          {successMessage && !orgCodeMessage && (
            <div className="success-message" style={{ 
              backgroundColor: '#e8f5e9',
              color: '#2e7d32',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              whiteSpace: 'pre-line',
              border: '1px solid #81c784'
            }}>
              {successMessage}
            </div>
          )}

          {!showResetForm ? (
            <>
              <div className={styles.field}>
                <label htmlFor="email">{t('login.email')}</label>
                  <PlatformInput
                  type="email"
                  id="email"
                  name="email"
                  value={credentials.email}
                  onChange={handleChange}
                  placeholder={t('login.email')}
                  required
                  disabled={isLoading}
                />
                {fieldErrors.email && (
                  <div className="field-error">{fieldErrors.email}</div>
                )}
              </div>

              <div style={{ marginBottom: '.1px', width: '100%', textAlign: 'left', fontWeight: '600' }}>
                <label htmlFor="password">{t('login.password')}</label>
                  <PlatformInput
                  type="password"
                  id="password"
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  placeholder={t('login.password')}
                  required
                  disabled={isLoading}
                />
              </div>

                  <div style={{ marginBottom: '12px', width: '100%', textAlign: 'left' }}>
                <span
                  role="link"
                  tabIndex={0}
                  style={{
                    color: isIOS() ? '#007AFF' : isAndroid() ? '#007AFF' : '#1976D2',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontWeight: 600
                  }}
                  onClick={e => {
                    e.preventDefault();
                    setShowResetForm(true);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setShowResetForm(true);
                    }
                  }}
                >
                  {t('login.forgotPassword')}
                </span>
              </div>

                <PlatformButton 
                type="submit" 
                className={styles.submit}
                disabled={isLoading || orgLoading}
              >
                {isLoading || orgLoading ? t('login.signingIn') : t('login.signIn')}
                </PlatformButton>

              <div className="auth-footer">
                <p>
                  {t('login.noAccount')}{' '}
                  <Link to="/register" className="auth-link">
                    {t('login.signUp')}
                  </Link>
                </p>
                {!isNativeIOS && (
                  <p>
                    {t('login.createOrganization')}{' '}
                    <Link to="/register-organization" className="auth-link">
                      {t('login.registerOrganization')}
                    </Link>
                  </p>
                )}
                <div className="legal-links-footer">
                  <Link to="/privacy-policy" className="legal-link">
                    Privacy Policy
                  </Link>
                  <span className="legal-separator">•</span>
                  <Link to="/terms-of-service" className="legal-link">
                    Terms of Service
                  </Link>
                </div>
              </div>
            </>
          ) : (
            <div className={forgotPasswordStyles.formWrapper}>
                <h1 className={forgotPasswordStyles.formTitle}>{t('passwordReset.title')}</h1>
                <p className={forgotPasswordStyles.formSubtitle}>{t('passwordReset.subtitle')}</p>
              
              <div className={forgotPasswordStyles.field}>
                <label htmlFor="resetEmail">{t('passwordReset.email')}</label>
                  <PlatformInput
                  type="email"
                  id="resetEmail"
                  name="resetEmail"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder={t('passwordReset.email')}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className={forgotPasswordStyles.actions}>
                  <PlatformButton 
                  type="button" 
                  className={forgotPasswordStyles.cancel}
                  onClick={() => {
                    setShowResetForm(false);
                    setResetEmail('');
                    setError('');
                  }}
                  disabled={isLoading}
                >
                  {tCommon('cancel')}
                  </PlatformButton>
                  <PlatformButton 
                  type="submit" 
                  className={forgotPasswordStyles.submit}
                  onClick={handlePasswordReset}
                  disabled={isLoading}
                >
                  {isLoading ? t('passwordReset.sendingReset') : t('passwordReset.sendReset')}
                  </PlatformButton>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
    </PlatformLayout>
    </ErrorBoundary>
  );
}

export default LoginForm;
